import React, { useState } from 'react'
import { FileQuestion, Download, Search, Filter } from 'lucide-react'

interface Questionnaire {
  id: string
  name: string
  type: 'SOC2' | 'ISO27001' | 'CAIQ' | 'SIG' | 'GDPR' | 'HIPAA' | 'Custom'
  questions: number
  lastUpdated: string
  completionRate: number
}

const QuestionnaireLibrary = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState('all')

  // Mock data - in production this would come from API
  const questionnaires: Questionnaire[] = [
    {
      id: '1',
      name: 'SOC 2 Type II Security Questionnaire',
      type: 'SOC2',
      questions: 125,
      lastUpdated: '2025-01-15',
      completionRate: 100
    },
    {
      id: '2',
      name: 'ISO 27001 Compliance Questionnaire',
      type: 'ISO27001',
      questions: 98,
      lastUpdated: '2025-01-10',
      completionRate: 95
    },
    {
      id: '3',
      name: 'CSA CAIQ v4.0.3',
      type: 'CAIQ',
      questions: 261,
      lastUpdated: '2025-02-01',
      completionRate: 0
    },
    {
      id: '4',
      name: 'Standard Information Gathering (SIG) Lite',
      type: 'SIG',
      questions: 142,
      lastUpdated: '2025-01-05',
      completionRate: 100
    },
    {
      id: '5',
      name: 'GDPR Data Processing Assessment',
      type: 'GDPR',
      questions: 67,
      lastUpdated: '2025-01-12',
      completionRate: 100
    },
    {
      id: '6',
      name: 'HIPAA Security Rule Compliance',
      type: 'HIPAA',
      questions: 89,
      lastUpdated: '2024-11-30',
      completionRate: 92
    }
  ]

  const filteredQuestionnaires = questionnaires.filter(q => {
    const matchesSearch = q.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         q.type.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesType = filterType === 'all' || q.type === filterType
    return matchesSearch && matchesType
  })

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <FileQuestion className="h-8 w-8 text-primary-600" />
              <h1 className="ml-3 text-2xl font-bold text-gray-900">Questionnaire Library</h1>
            </div>
            <a href="/admin" className="text-primary-600 hover:text-primary-700">← Back</a>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">Pre-Answered Security Questionnaires</h3>
          <p className="text-sm text-blue-800">
            Download pre-completed questionnaires with answers based on your trust center documentation.
            Saves 80%+ time on vendor security reviews.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search questionnaires..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Types</option>
              <option value="SOC2">SOC 2</option>
              <option value="ISO27001">ISO 27001</option>
              <option value="CAIQ">CAIQ</option>
              <option value="SIG">SIG</option>
              <option value="GDPR">GDPR</option>
              <option value="HIPAA">HIPAA</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredQuestionnaires.map(q => (
            <div key={q.id} className="bg-white rounded-lg shadow hover:shadow-lg transition p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <span className="inline-block px-3 py-1 bg-primary-100 text-primary-800 text-xs font-semibold rounded-full mb-2">
                    {q.type}
                  </span>
                  <h3 className="text-lg font-semibold text-gray-900">{q.name}</h3>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Questions:</span>
                  <span className="font-medium text-gray-900">{q.questions}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Last Updated:</span>
                  <span className="font-medium text-gray-900">
                    {new Date(q.lastUpdated).toLocaleDateString()}
                  </span>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Completion:</span>
                    <span className="font-medium text-gray-900">{q.completionRate}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full"
                      style={{ width: `${q.completionRate}%` }}
                    />
                  </div>
                </div>
              </div>

              <button className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2 px-4 rounded-lg transition inline-flex items-center justify-center">
                <Download className="h-4 w-4 mr-2" />
                Download Questionnaire
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default QuestionnaireLibrary
